import { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  alpha,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ExtensionIcon from '@mui/icons-material/Extension';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SettingsIcon from '@mui/icons-material/Settings';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BusinessIcon from '@mui/icons-material/Business';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useOrgModules, useDisableModule, useEnableModule } from '../../hooks/subscriptions';
import {
  useModuleDependencies,
  useModuleDependents,
  useValidateDisableModule,
} from '../../hooks/subscriptions/useModuleDependencies';
import { getCategoryLabel, getCategoryColor, isModuleTrial, formatTrialCountdown } from '../../utils/subscriptions';
import { EnableModuleDialog } from './EnableModuleDialog';
import { CascadeDisableDialog } from './CascadeDisableDialog';
import { useSnackbar } from '../SnackbarProvider';
import type { OrganizationModule, ValidateDisableModuleResponse } from '../../types/subscriptions';
import { ModuleCategory } from '../../types/subscriptions';

interface OrganizationModulesTabProps {
  organizationId: string;
  organizationName?: string;
}

const StyledSwitch = styled(Switch)(({ theme }) => ({
  width: 42,
  height: 24,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(18px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
        opacity: 1,
        border: 0,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: 20,
    height: 20,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  },
  '& .MuiSwitch-track': {
    borderRadius: 12,
    backgroundColor: '#e3e8ef',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 300,
    }),
  },
}));

const StyledTableRow = styled(TableRow)<{ isActive: boolean }>(({ isActive }) => ({
  backgroundColor: isActive ? '#fff' : '#fafbfc',
  opacity: isActive ? 1 : 0.7,
  '&:hover': {
    backgroundColor: isActive ? alpha('#667eea', 0.04) : '#f5f5f5',
  },
  '& > td': {
    borderBottom: '1px solid #e3e8ef',
  },
}));

const getModuleIcon = (category: string) => {
  const icons: Record<string, any> = {
    CORE: SettingsIcon,
    OPERATIONS: StorefrontIcon,
    SALES: BusinessIcon,
    LOGISTICS: LocalShippingIcon,
    ANALYTICS: AnalyticsIcon,
    INTEGRATIONS: ExtensionIcon,
  };
  return icons[category] || ExtensionIcon;
};

const getModuleGradient = (category: string) => {
  const gradients: Record<string, string> = {
    CORE: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    OPERATIONS: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
    SALES: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
    LOGISTICS: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
    ANALYTICS: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
    INTEGRATIONS: 'linear-gradient(135deg, #FF5722 0%, #E64A19 100%)',
  };
  return gradients[category] || 'linear-gradient(135deg, #697386 0%, #5a6479 100%)';
};

// Helper function to determine if module is from tier or add-on
const getModuleSourceInfo = (module: { tierMinimum?: string | null; isAddOn?: boolean }) => {
  if (module.isAddOn) {
    return {
      label: 'Add-on',
      type: 'addon' as const,
      color: '#9C27B0', // Purple for add-ons
      icon: AddCircleOutlineIcon,
    };
  }
  if (module.tierMinimum) {
    const tierLabels: Record<string, string> = {
      free: 'Free Tier',
      starter: 'Starter Tier',
      professional: 'Professional Tier',
      enterprise: 'Enterprise Tier',
    };
    return {
      label: tierLabels[module.tierMinimum] || 'Tier',
      type: 'tier' as const,
      color: '#1976D2', // Blue for tier-based
      icon: WorkspacePremiumIcon,
    };
  }
  return null;
};

// Compact dependency display for list view
const CompactDependencyDisplay = ({ moduleKey }: { moduleKey: string }) => {
  const { data: dependencies } = useModuleDependencies(moduleKey, true);
  const { data: dependents } = useModuleDependents(moduleKey, true);

  const hasDependencies = dependencies && dependencies.length > 0;
  const hasDependents = dependents && dependents.length > 0;

  if (!hasDependencies && !hasDependents) {
    return <Typography variant="caption" color="text.secondary">—</Typography>;
  }

  return (
    <Stack spacing={0.5}>
      {hasDependencies && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
          <Tooltip title="Requires">
            <ArrowForwardIcon sx={{ fontSize: 12, color: '#2196F3' }} />
          </Tooltip>
          {dependencies.map((dep) => (
            <Chip
              key={dep.id}
              label={dep.name}
              size="small"
              sx={{
                backgroundColor: alpha('#2196F3', 0.1),
                color: '#2196F3',
                border: `1px solid ${alpha('#2196F3', 0.3)}`,
                fontSize: '10px',
                height: '20px',
                fontWeight: 600,
              }}
            />
          ))}
        </Stack>
      )}
      {hasDependents && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
          <Tooltip title="Required by">
            <ArrowBackIcon sx={{ fontSize: 12, color: '#FF9800' }} />
          </Tooltip>
          {dependents.map((dep) => (
            <Chip
              key={dep.id}
              label={dep.name}
              size="small"
              sx={{
                backgroundColor: alpha('#FF9800', 0.1),
                color: '#FF9800',
                border: `1px solid ${alpha('#FF9800', 0.3)}`,
                fontSize: '10px',
                height: '20px',
                fontWeight: 600,
              }}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export const OrganizationModulesTab = ({
  organizationId,
  organizationName = 'this organization',
}: OrganizationModulesTabProps) => {
  const { showMessage } = useSnackbar();
  const [enableDialogOpen, setEnableDialogOpen] = useState(false);
  const [disableConfirmOpen, setDisableConfirmOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState<OrganizationModule | null>(null);

  // Cascade disable dialog state
  const [cascadeDialogOpen, setCascadeDialogOpen] = useState(false);
  const [cascadeDialogData, setCascadeDialogData] = useState<ValidateDisableModuleResponse | null>(null);

  const { data: orgModules, isLoading, error } = useOrgModules(organizationId);
  const disableMutation = useDisableModule(organizationId);
  const enableMutation = useEnableModule(organizationId);
  const validateDisableMutation = useValidateDisableModule();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress sx={{ color: '#667eea' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: '12px' }}>
        Failed to load modules information
      </Alert>
    );
  }

  if (!orgModules || orgModules.length === 0) {
    return (
      <Alert
        severity="info"
        sx={{
          borderRadius: '12px',
          border: '1px solid #2196F3',
          backgroundColor: alpha('#2196F3', 0.05),
        }}
      >
        No modules are currently enabled for this organization. Modules are typically enabled based
        on the assigned subscription plan.
      </Alert>
    );
  }

  const activeModules = orgModules.filter((om) => !om.disabledAt);
  const disabledModules = orgModules.filter((om) => om.disabledAt);

  const handleDisableClick = (orgModule: OrganizationModule) => {
    if (!orgModule.module?.key) return;

    // Validate disable operation first
    validateDisableMutation.mutate(orgModule.module.key, {
      onSuccess: (result) => {
        if (!result.valid && result.affectedModules.length > 0) {
          // Has dependents - show cascade dialog
          setSelectedModule(orgModule);
          setCascadeDialogData(result);
          setCascadeDialogOpen(true);
        } else {
          // Safe to disable - show simple confirm dialog
          setSelectedModule(orgModule);
          setDisableConfirmOpen(true);
        }
      },
      onError: (error: any) => {
        showMessage(
          error.response?.data?.message || 'Failed to validate module disable',
          'error'
        );
      },
    });
  };

  const handleDisableConfirm = () => {
    if (selectedModule?.module?.key) {
      disableMutation.mutate(
        { moduleKey: selectedModule.module.key, force: false },
        {
          onSuccess: () => {
            showMessage('Module disabled successfully', 'success');
            setDisableConfirmOpen(false);
            setSelectedModule(null);
          },
          onError: (error: any) => {
            showMessage(error.response?.data?.message || 'Failed to disable module', 'error');
          },
        }
      );
    }
  };

  const handleCascadeDisableConfirm = (force: boolean) => {
    if (!selectedModule?.module?.key) return;

    // Disable with force flag
    disableMutation.mutate(
      { moduleKey: selectedModule.module.key, force },
      {
        onSuccess: (response) => {
          const cascadeCount = response?.cascadeDisabledModules?.length || 0;
          let message = 'Module disabled successfully';
          if (cascadeCount > 0) {
            message += ` (+ ${cascadeCount} ${cascadeCount === 1 ? 'dependent' : 'dependents'} disabled)`;
          }
          showMessage(message, 'success');
          setCascadeDialogOpen(false);
          setSelectedModule(null);
          setCascadeDialogData(null);
        },
        onError: (error: any) => {
          showMessage(error.response?.data?.message || 'Failed to disable module', 'error');
        },
      }
    );
  };

  const handleEnableClick = (orgModule: OrganizationModule) => {
    if (orgModule?.module?.key) {
      enableMutation.mutate(
        {
          moduleKey: orgModule.module.key,
          isTrial: false,
        },
        {
          onSuccess: () => {
            showMessage('Module enabled successfully', 'success');
          },
          onError: (error: any) => {
            showMessage(error.response?.data?.message || 'Failed to enable module', 'error');
          },
        }
      );
    }
  };

  const handleToggle = (orgModule: OrganizationModule) => {
    if (orgModule.disabledAt) {
      handleEnableClick(orgModule);
    } else {
      handleDisableClick(orgModule);
    }
  };

  const allModules = [...activeModules, ...disabledModules];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: '#1a1f36',
              mb: 0.5,
            }}
          >
            Feature Modules
          </Typography>
          <Typography variant="body2" color="#697386">
            {activeModules.length} of {orgModules.length} modules active
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setEnableDialogOpen(true)}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '10px',
            padding: '10px 24px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '14px',
            boxShadow: '0 4px 14px 0 rgba(102, 126, 234, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              boxShadow: '0 6px 20px 0 rgba(102, 126, 234, 0.4)',
              transform: 'translateY(-2px)',
            },
          }}
        >
          Enable Module
        </Button>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: '12px',
          border: '1px solid #e3e8ef',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
              <TableCell sx={{ fontWeight: 700, color: '#697386', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', width: '50px' }}>
                Status
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#697386', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Module
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#697386', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', width: '130px' }}>
                Category
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#697386', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', width: '120px' }}>
                Tier
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#697386', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Dependencies
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#697386', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', width: '130px' }}>
                Date
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#697386', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', width: '80px', textAlign: 'center' }}>
                Toggle
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allModules.map((orgModule) => {
              const isActive = !orgModule.disabledAt;
              const category = orgModule.module?.category || ModuleCategory.CORE;
              const IconComponent = getModuleIcon(category);
              const gradient = getModuleGradient(category);
              const sourceInfo = orgModule.module ? getModuleSourceInfo(orgModule.module) : null;

              return (
                <StyledTableRow key={orgModule.id} isActive={isActive}>
                  {/* Status */}
                  <TableCell>
                    <Tooltip title={isActive ? 'Active' : 'Disabled'}>
                      {isActive ? (
                        <CheckCircleIcon sx={{ fontSize: 20, color: '#4CAF50' }} />
                      ) : (
                        <CancelIcon sx={{ fontSize: 20, color: '#9e9e9e' }} />
                      )}
                    </Tooltip>
                  </TableCell>

                  {/* Module Name & Description */}
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '10px',
                          background: gradient,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <IconComponent sx={{ fontSize: 20, color: '#fff' }} />
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1f36' }}>
                          {orgModule.module?.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#697386', display: 'block', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {orgModule.module?.description}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  {/* Category */}
                  <TableCell>
                    {orgModule.module?.category && (
                      <Chip
                        label={getCategoryLabel(orgModule.module.category)}
                        size="small"
                        sx={{
                          backgroundColor: alpha(getCategoryColor(orgModule.module.category), 0.12),
                          color: getCategoryColor(orgModule.module.category),
                          border: `1px solid ${alpha(getCategoryColor(orgModule.module.category), 0.3)}`,
                          fontWeight: 600,
                          fontSize: '10px',
                          height: '22px',
                        }}
                      />
                    )}
                  </TableCell>

                  {/* Tier */}
                  <TableCell>
                    {sourceInfo ? (
                      <Chip
                        icon={<sourceInfo.icon sx={{ fontSize: '12px !important' }} />}
                        label={sourceInfo.label}
                        size="small"
                        sx={{
                          backgroundColor: alpha(sourceInfo.color, 0.12),
                          color: sourceInfo.color,
                          border: `1px solid ${alpha(sourceInfo.color, 0.3)}`,
                          fontWeight: 600,
                          fontSize: '10px',
                          height: '22px',
                          '& .MuiChip-icon': { color: sourceInfo.color },
                        }}
                      />
                    ) : orgModule.module?.isCore ? (
                      <Chip
                        label="Core"
                        size="small"
                        sx={{
                          backgroundColor: alpha('#667eea', 0.12),
                          color: '#667eea',
                          border: `1px solid ${alpha('#667eea', 0.3)}`,
                          fontWeight: 600,
                          fontSize: '10px',
                          height: '22px',
                        }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                    {isModuleTrial(orgModule) && (
                      <Chip
                        label={`Trial: ${formatTrialCountdown(orgModule.trialEndsAt)}`}
                        size="small"
                        sx={{
                          ml: 0.5,
                          backgroundColor: alpha('#2196F3', 0.12),
                          color: '#2196F3',
                          border: `1px solid ${alpha('#2196F3', 0.3)}`,
                          fontWeight: 600,
                          fontSize: '10px',
                          height: '22px',
                        }}
                      />
                    )}
                  </TableCell>

                  {/* Dependencies */}
                  <TableCell>
                    {orgModule.module?.key && (
                      <CompactDependencyDisplay moduleKey={orgModule.module.key} />
                    )}
                  </TableCell>

                  {/* Date */}
                  <TableCell>
                    <Typography variant="caption" sx={{ color: '#697386' }}>
                      {new Date(
                        isActive ? orgModule.enabledAt : orgModule.disabledAt!
                      ).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </Typography>
                  </TableCell>

                  {/* Toggle */}
                  <TableCell align="center">
                    <StyledSwitch
                      checked={isActive}
                      onChange={() => handleToggle(orgModule)}
                      disabled={disableMutation.isPending || enableMutation.isPending}
                    />
                  </TableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialogs */}
      <EnableModuleDialog
        open={enableDialogOpen}
        onClose={() => setEnableDialogOpen(false)}
        organizationId={organizationId}
        organizationName={organizationName}
      />

      <Dialog
        open={disableConfirmOpen}
        onClose={() => !disableMutation.isPending && setDisableConfirmOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            padding: '8px',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '20px', color: '#1a1f36' }}>
          Disable Module?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#697386', mb: 2 }}>
            Are you sure you want to disable <strong>{selectedModule?.module?.name}</strong> for{' '}
            <strong>{organizationName}</strong>?
          </Typography>
          <Alert
            severity="warning"
            sx={{
              borderRadius: '12px',
              border: '1px solid #FF9800',
              backgroundColor: alpha('#FF9800', 0.05),
            }}
          >
            This will immediately remove access to this module for the organization.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={() => setDisableConfirmOpen(false)}
            disabled={disableMutation.isPending}
            sx={{
              textTransform: 'none',
              color: '#697386',
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDisableConfirm}
            disabled={disableMutation.isPending}
            sx={{
              textTransform: 'none',
              backgroundColor: '#f44336',
              color: '#fff',
              fontWeight: 600,
              borderRadius: '8px',
              padding: '8px 20px',
              '&:hover': {
                backgroundColor: '#d32f2f',
              },
            }}
          >
            {disableMutation.isPending ? 'Disabling...' : 'Disable'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cascade Disable Dialog */}
      {cascadeDialogData && selectedModule?.module && (
        <CascadeDisableDialog
          open={cascadeDialogOpen}
          onClose={() => {
            setCascadeDialogOpen(false);
            setCascadeDialogData(null);
            setSelectedModule(null);
          }}
          module={{
            id: selectedModule.module.id,
            key: selectedModule.module.key,
            name: selectedModule.module.name,
            description: selectedModule.module.description,
            category: selectedModule.module.category,
            isCore: selectedModule.module.isCore,
          }}
          affectedModules={cascadeDialogData.affectedModules}
          willCascadeDisable={cascadeDialogData.willCascadeDisable}
          onConfirm={handleCascadeDisableConfirm}
          isLoading={disableMutation.isPending}
        />
      )}
    </Box>
  );
};
